// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { FunctionComponent } from 'react'
import { Location, useLocation, useNavigate } from 'react-router-dom'
import { Nav } from '@teamnine/pcj-ui-v2'
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Group,
  Description,
} from '@/utils/material-icons'

import { useLayout } from '@/app/shared/components/Layout/layout-context'
import * as Styled from './Styles'

type NavigationItem = {
  label: string
  icon: FunctionComponent
  path: string
}

const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    label: 'Customer Management',
    icon: Group,
    path: '/customer-management',
  },
  {
    label: 'Templates',
    icon: Description,
    path: '/templates',
  },
]

const getActivePath = (path: string, location: Location) => {
  if (path === '/') {
    return location.pathname === path
  }

  return location.pathname.startsWith(path)
}

export type SideNavContentProps = {
  isCollapsed: boolean
  handleCollapse: (arg: boolean) => void
  onNavigate?: () => void
}

export default function SideNavContent({
  handleCollapse,
  isCollapsed,
  onNavigate,
}: SideNavContentProps) {
  const { useAside, isFullScreen } = useLayout()
  const navigate = useNavigate()
  const location = useLocation()
  const CollapseIcon = isCollapsed
    ? KeyboardDoubleArrowRight
    : KeyboardDoubleArrowLeft

  const handleAsideNav = (path: string) => {
    navigate(path)
    if (useAside) {
      onNavigate?.()
    }
  }

  return (
    <Styled.Nav isCollapsed={isCollapsed} isFullScreen={isFullScreen}>
      <Styled.CollapseButton
        onClick={() => handleCollapse(!isCollapsed)}
        icon={<CollapseIcon />}
        aria-label="Toggle navigation"
        $isCollapsed={isCollapsed}
        $isFullScreen={isFullScreen}
      />
      <Nav.Content gap={16}>
        {NAVIGATION_ITEMS.map(({ path, icon, label }) => (
          <Nav.Item
            key={path}
            icon={icon}
            onClick={() => handleAsideNav(path)}
            isActive={getActivePath(path, location)}
            aria-label={label}
            maxLines={path === '/customer-management' ? 2 : undefined}
          >
            {label}
          </Nav.Item>
        ))}
      </Nav.Content>
    </Styled.Nav>
  )
}
